import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

if (!customElements.get('cm-duos')) {
  class CMDuos extends Component {
    static confirmed(before, after, selections) {
      if (!before?.items || !after?.items) return false;
      const expected = new Map();
      selections.forEach(({ variant }) => expected.set(variant.id, (expected.get(variant.id) || 0) + 1));
      const quantityOf = (cart, id) => cart.items.filter(item => item.variant_id === id).reduce((sum, item) => sum + item.quantity, 0);
      return [...expected].every(([id, quantity]) => quantityOf(after, id) >= quantityOf(before, id) + quantity);
    }
    static quote(selections, offer, currency) {
      const valid = selections.length === 2 && selections.every(({ variant }) => variant?.available && Number.isFinite(variant.price) && variant.price > 0);
      const regular = valid ? selections.reduce((sum, { variant }) => sum + variant.price, 0) : 0;
      const promotional = valid && offer?.enabled === true && currency === 'EUR' && offer.currency === 'EUR'
        && offer.baseTotal === 9980 && offer.total === 6990 && regular === 9980
        && selections.every(({ variant }) => variant.price === 4990);
      return { valid, regular, total: promotional ? offer.total : regular, promotional };
    }
    connectedCallback() {
      super.connectedCallback();
      this.initialize();
    }
    updatedCallback() {
      super.updatedCallback();
      this.controller?.abort();
      this.ready = false;
      this.initialize();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.ready = false;
      super.disconnectedCallback();
    }
    listen(element, event, handler) {
      element.addEventListener(event, handler, { signal: this.controller.signal });
    }
    initialize() {
      if (this.ready) return;
      try {
        const data = this.querySelector('[data-duo-data]');
        const config = JSON.parse(data?.textContent || '');
        if (!Array.isArray(config.products) || !config.strings || typeof config.strings !== 'object' || Array.isArray(config.strings)) throw new Error('Invalid duo configuration');
        const products = config.products.map(({ product, url, image, ticker }) => {
          if (!product || !Number.isSafeInteger(Number(product.id)) || !(Number(product.id) > 0) || typeof product.title !== 'string'
            || typeof product.handle !== 'string' || !Array.isArray(product.variants)
            || product.variants.some(variant => !Number.isSafeInteger(Number(variant.id)) || !(Number(variant.id) > 0)
              || typeof variant.available !== 'boolean' || !Number.isFinite(variant.price))) throw new Error('Invalid duo product');
          return { ...product, id: Number(product.id), variants: product.variants.map(variant => ({ ...variant, id: Number(variant.id) })), url, image, ticker };
        });
        const slots = [...this.querySelectorAll('[data-duo-slot]')];
        const form = this.querySelector('[data-duo-form]');
        const button = this.querySelector('[data-duo-add]');
        const status = this.querySelector('[data-duo-status]');
        const cartLink = this.querySelector('[data-duo-cart]');
        if (!form || !button || !status || !cartLink || slots.length !== 2
          || slots.some(slot => ['[data-duo-product]', '[data-duo-variant]', '[data-duo-variant-wrap]', '[data-duo-image]', '[data-duo-price]'].some(selector => !slot.querySelector(selector)))
          || !this.querySelector('[data-duo-empty]') || !this.querySelector('[data-duo-builder]')
          || !this.querySelector('[data-duo-pairings]') || !this.querySelector('[data-duo-total]')) throw new Error('Incomplete duo markup');
        const currency = this.dataset.currency || 'EUR';
        const format = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency });
        this.config = config;
        this.text = config.strings;
        this.products = [...new Map(products.map(product => [String(product.id), product])).values()]
          .filter(product => !product.requires_selling_plan && product.variants.some(variant => variant.available && variant.price > 0));
        this.slots = slots;
        this.form = form;
        this.button = button;
        this.status = status;
        this.cartLink = cartLink;
        this.currency = currency;
        this.format = format;
        this.root = `${(this.dataset.root || '/').replace(/\/$/, '')}/`;
        this.enhance();
        this.ready = true;
        this.querySelectorAll('[data-duo-product-wrap]').forEach(wrap => { wrap.hidden = false; });
        const error = this.querySelector('[data-duo-error]');
        if (error) error.hidden = true;
      } catch (error) {
        // Leave the server-rendered native product form available if enhancement fails.
        this.controller?.abort();
        this.ready = false;
        const message = this.querySelector('[data-duo-error]');
        if (message) message.hidden = false;
        this.querySelectorAll('[data-duo-product-wrap]').forEach(wrap => { wrap.hidden = true; });
        console.warn('[cm-duos] Native form retained:', error);
      } finally {
        const loading = this.querySelector('[data-duo-loading]');
        if (loading) loading.hidden = true;
      }
    }
    enhance() {
      this.controller = new AbortController();
      this.busy = false;
      this.completed = false;
      this.needsCartReview = false;
      this.querySelector('[data-duo-empty]').hidden = this.products.length > 0;
      this.querySelector('[data-duo-builder]').hidden = this.products.length === 0;
      this.querySelector('[data-duo-pairings]').hidden = true;
      this.querySelectorAll('[data-duo-pair]').forEach(pair => { pair.hidden = true; });
      if (!this.products.length) return;
      this.slots.forEach((slot, index) => {
        const select = slot.querySelector('[data-duo-product]');
        const previousVariantId = slot.querySelector('[data-duo-variant]').value;
        const previousProduct = this.products.find(product => product.variants.some(variant => String(variant.id) === previousVariantId && variant.available && variant.price > 0));
        select.replaceChildren();
        this.products.forEach(product => select.add(new Option(product.title, String(product.id))));
        select.value = String((previousProduct || this.products[Math.min(index, this.products.length - 1)]).id);
        this.listen(select, 'change', () => { this.renderSlot(index); this.renderTotal(); if (!this.needsCartReview) this.status.textContent = ''; });
        this.listen(slot.querySelector('[data-duo-variant]'), 'change', () => { this.renderVariant(index); this.renderTotal(); if (!this.needsCartReview) this.status.textContent = ''; });
        this.renderSlot(index, previousVariantId);
      });
      this.querySelectorAll('[data-duo-pair]').forEach(pair => {
        if (!this.products.some(product => String(product.id) === pair.dataset.first) || !this.products.some(product => String(product.id) === pair.dataset.second)) return;
        pair.hidden = false;
        this.querySelector('[data-duo-pairings]').hidden = false;
        this.listen(pair, 'click', () => {
          [pair.dataset.first, pair.dataset.second].forEach((id, index) => {
            this.slots[index].querySelector('[data-duo-product]').value = id;
            this.renderSlot(index);
          });
          this.renderTotal();
          this.slots[0].querySelector('select').focus();
          this.slots[0].scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        });
      });
      this.listen(this.form, 'submit', event => { event.preventDefault(); this.add(); });
      this.renderTotal();
    }
    selection(index) {
      const slot = this.slots[index];
      const product = this.products.find(item => String(item.id) === slot.querySelector('[data-duo-product]').value);
      return { product, variant: product?.variants.find(item => String(item.id) === slot.querySelector('[data-duo-variant]').value) };
    }
    renderSlot(index, preferredVariantId, requirePreviousSelection = false) {
      const slot = this.slots[index];
      const product = this.products.find(item => String(item.id) === slot.querySelector('[data-duo-product]').value);
      const select = slot.querySelector('[data-duo-variant]');
      select.replaceChildren();
      if (!product) {
        slot.querySelector('[data-duo-variant-wrap]').hidden = true;
        this.renderVariant(index);
        return;
      }
      if (requirePreviousSelection) {
        const placeholder = new Option(this.text.duo_variant, '');
        placeholder.disabled = true;
        select.add(placeholder);
      }
      product.variants.forEach(variant => {
        const available = variant.available && variant.price > 0;
        const label = `${variant.title} — ${variant.price > 0 ? this.format.format(variant.price / 100) : this.text.soon}${available ? '' : ` · ${this.text.sold_out}`}`;
        const option = new Option(label, String(variant.id));
        option.disabled = !available;
        select.add(option);
      });
      const previous = product.variants.find(variant => String(variant.id) === String(preferredVariantId) && variant.available && variant.price > 0);
      const first = previous || (!requirePreviousSelection && product.variants.find(variant => variant.available && variant.price > 0));
      select.value = first ? String(first.id) : '';
      slot.querySelector('[data-duo-variant-wrap]').hidden = product.variants.length === 1 && Boolean(first);
      this.renderVariant(index);
    }
    reconcileProducts(freshProducts, selections) {
      const freshById = new Map(freshProducts.map(product => [String(product.id), product]));
      this.products = this.products.map(product => {
        const fresh = freshById.get(String(product.id));
        return fresh ? { ...product, ...fresh, url: product.url, image: product.image } : product;
      }).filter(product => !product.requires_selling_plan && product.variants.some(variant => variant.available && variant.price > 0));
      this.querySelector('[data-duo-empty]').hidden = this.products.length > 0;
      this.querySelector('[data-duo-builder]').hidden = this.products.length === 0;
      this.slots.forEach((slot, index) => {
        const select = slot.querySelector('[data-duo-product]');
        select.replaceChildren();
        const placeholder = new Option(this.text.duo_choose, '');
        placeholder.disabled = true;
        select.add(placeholder);
        this.products.forEach(product => select.add(new Option(product.title, String(product.id))));
        const previous = selections[index];
        select.value = this.products.some(product => product.id === previous.product.id) ? String(previous.product.id) : '';
        this.renderSlot(index, previous.variant.id, true);
      });
      let hasPairings = false;
      this.querySelectorAll('[data-duo-pair]').forEach(pair => {
        pair.hidden = !this.products.some(product => String(product.id) === pair.dataset.first) || !this.products.some(product => String(product.id) === pair.dataset.second);
        if (!pair.hidden) hasPairings = true;
      });
      this.querySelector('[data-duo-pairings]').hidden = !hasPairings;
    }
    renderVariant(index) {
      const slot = this.slots[index];
      const { product, variant } = this.selection(index);
      const ticker = slot.querySelector('[data-duo-ticker]');
      if (ticker) ticker.textContent = product?.ticker || '—';
      const img = slot.querySelector('[data-duo-image]');
      const source = variant?.featured_image?.src || product?.image;
      if (source) {
        // Liquid's initial responsive candidates belong to the previous product.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = source;
        img.alt = product.title;
        img.hidden = false;
      } else { img.hidden = true; }
      slot.querySelector('[data-duo-price]').textContent = variant?.price > 0 ? this.format.format(variant.price / 100) : this.text.soon;
      if (variant) {
        const option = [...slot.querySelector('[data-duo-variant]').options].find(item => item.value === String(variant.id));
        if (option) {
          option.textContent = `${variant.title} — ${variant.price > 0 ? this.format.format(variant.price / 100) : this.text.soon}${variant.available && variant.price > 0 ? '' : ` · ${this.text.sold_out}`}`;
          option.disabled = !variant.available || variant.price <= 0;
        }
      }
    }
    renderTotal() {
      const selections = this.slots.map((_, index) => this.selection(index));
      const { valid, regular, total, promotional } = CMDuos.quote(selections, this.config?.offer, this.currency || this.dataset.currency || 'EUR');
      this.button.disabled = !valid || this.busy || this.completed || this.needsCartReview;
      this.querySelector('[data-duo-total]').textContent = valid ? this.format.format(total / 100) : '—';
      const regularTotal = this.querySelector('[data-duo-regular-total]');
      if (regularTotal) { regularTotal.textContent = valid ? this.format.format(regular / 100) : ''; regularTotal.hidden = !promotional; }
      const pairLabel = this.querySelector('[data-duo-pair-label]');
      if (pairLabel) pairLabel.textContent = selections.map(({ product }) => product?.ticker || '—').join(' / ');
      for (const [selector, key] of [['[data-duo-offer-label]', 'duo_offer_label'], ['[data-duo-offer-note]', 'duo_offer_note']]) {
        const node = this.querySelector(selector);
        if (node) { if (this.text[key]) node.textContent = this.text[key]; node.hidden = !promotional; }
      }
      const fourOffer = this.querySelector('[data-duo-four-offer]');
      const offer = this.config?.offer;
      const showFourOffer = promotional && offer?.fourEnabled === true && offer.fourTotal === 11990 && offer.fourBaseTotal === 19960;
      if (fourOffer) fourOffer.hidden = !showFourOffer;
      for (const [selector, amount] of [['[data-duo-four-total]', offer?.fourTotal], ['[data-duo-four-regular]', offer?.fourBaseTotal]]) {
        const node = this.querySelector(selector);
        if (node) node.textContent = showFourOffer ? this.format.format(amount / 100) : '';
      }
    }
    async fetchJSON(url, options = {}) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: this.controller.signal, ...options });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || this.text.duo_error);
      return payload;
    }
    async add() {
      if (this.busy || this.button.disabled) return;
      const selections = this.slots.map((_, index) => this.selection(index));
      if (selections.some(({ variant }) => !variant?.available || variant.price <= 0)) return;
      const operationSignal = this.controller.signal;
      this.busy = true;
      this.button.disabled = true;
      this.slots.forEach(slot => slot.querySelectorAll('select').forEach(select => { select.disabled = true; }));
      this.querySelectorAll('[data-duo-pair]').forEach(pair => { pair.disabled = true; });
      this.cartLink.hidden = true;
      this.status.textContent = this.text.duo_loading;
      let cartBefore;
      let attempted = false;
      let cartEvent;
      let eventSettled = false;
      const settleCart = (cart, didError) => {
        if (!cartEvent || eventSettled) return;
        cartEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'cm-duos', sourceId: this.id || '', itemCount: cart.item_count, didError },
        });
        eventSettled = true;
      };
      try {
        const freshProducts = await Promise.all(selections.map(({ product }) => this.fetchJSON(`${this.root}products/${encodeURIComponent(product.handle)}.js`)));
        if (operationSignal.aborted) return;
        let priceChanged = false;
        let unavailable = false;
        freshProducts.forEach((fresh, index) => {
          const old = selections[index];
          const current = fresh.variants.find(variant => variant.id === old.variant.id);
          if (fresh.requires_selling_plan || !current?.available || current.price <= 0) unavailable = true;
          if (current && current.price !== old.variant.price) priceChanged = true;
        });
        this.reconcileProducts(freshProducts, selections);
        if (unavailable) {
          throw new Error(this.text.duo_unavailable);
        }
        if (priceChanged) {
          this.slots.forEach((_, index) => this.renderVariant(index));
          this.status.textContent = this.text.duo_price_changed;
          return;
        }
        cartBefore = await this.fetchJSON(`${this.root}cart.js`);
        if (operationSignal.aborted) return;
        const quantities = new Map();
        selections.forEach(({ variant }) => quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1));
        const items = [...quantities].map(([id, quantity]) => ({ id, quantity }));
        cartEvent = CartLinesUpdateEvent.createPromise();
        // A page may have no cart subscriber; keep rejected operation promises handled.
        cartEvent.promise.catch(() => {});
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add', context: 'product',
          lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
          promise: cartEvent.promise,
        }));
        attempted = true;
        await this.fetchJSON(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const cartAfter = await this.fetchJSON(`${this.root}cart.js`);
        if (!CMDuos.confirmed(cartBefore, cartAfter, selections)) throw new Error(this.text.duo_error);
        settleCart(cartAfter, false);
        this.completed = true;
        this.status.textContent = this.text.duo_added;
        window.location.assign(this.dataset.cart);
      } catch (error) {
        if (operationSignal.aborted) {
          if (cartEvent && !eventSettled) cartEvent.reject(error);
          return;
        }
        if (attempted) {
          try {
            const actualCart = await this.fetchJSON(`${this.root}cart.js`);
            if (CMDuos.confirmed(cartBefore, actualCart, selections)) {
              settleCart(actualCart, false);
              this.completed = true;
              this.status.textContent = this.text.duo_added;
              window.location.assign(this.dataset.cart);
              return;
            }
            settleCart(actualCart, true);
          } catch (cartError) {
            if (cartEvent && !eventSettled) { cartEvent.reject(cartError); eventSettled = true; }
          }
          if (operationSignal.aborted) return;
          this.dispatchEvent(new CartErrorEvent({ error: error.message || this.text.duo_error, code: 'INVALID', detail: { source: 'cm-duos' } }));
          // A failed HTTP response may still have added part of the selection.
          // Require a cart review instead of repeating an ambiguous mutation.
          this.needsCartReview = true;
          this.cartLink.hidden = false;
          this.status.textContent = this.text.duo_error;
        } else {
          this.status.textContent = error.message || this.text.duo_error;
        }
      } finally {
        if (operationSignal.aborted) return;
        this.busy = false;
        this.slots.forEach(slot => slot.querySelectorAll('select').forEach(select => { select.disabled = false; }));
        this.querySelectorAll('[data-duo-pair]').forEach(pair => { pair.disabled = false; });
        this.renderTotal();
      }
    }
  }
  customElements.define('cm-duos', CMDuos);
}
